import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { dms, inr, niceDate } from "./api";
import { PageHeader, EmptyState } from "./OwnerPages";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Handshake, TrendingUp, Store, Package, ArrowUpRight, ChevronRight, Trophy } from "lucide-react";

// ============================================================================
// Owner — Team Leader Performance Dashboard
// ============================================================================
export function OwnerTlPerformancePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    dms.ownerTlPerformance().then(d => setRows(d.data || [])).finally(() => setLoading(false));
  }, []);

  const topSales = rows[0]?.total_sales || 0;

  return (
    <div>
      <PageHeader title="Team Leader Performance" subtitle="Sales generated by each TL's assigned team" />
      {loading && <div className="text-sm text-slate-400 p-6">Loading…</div>}
      {!loading && rows.length === 0 && <EmptyState icon={Handshake} title="No team leaders yet" description="Create a Team Leader in User Management to see performance here" />}

      {/* Ranking cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((r, idx) => (
          <Card key={r.team_leader_id} className="p-5 relative">
            {idx === 0 && rows.length > 1 && (
              <div className="absolute -top-2 -right-2 h-8 w-8 rounded-full bg-amber-400 text-white flex items-center justify-center shadow-md" title="Top performer">
                <Trophy size={16} />
              </div>
            )}
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">#{idx + 1} · Team Leader</div>
                <div className="text-lg font-bold text-slate-900 mt-0.5">{r.name}</div>
                <div className="text-xs text-slate-500">{r.email}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Sales</div>
                <div className="text-xl font-bold text-[#8a6600]">{inr(r.total_sales)}</div>
              </div>
            </div>

            {/* Progress bar vs top performer */}
            <div className="mt-3">
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#faf6e6]0" style={{ width: `${topSales ? Math.max(2, (r.total_sales / topSales) * 100) : 0}%` }} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
              <MiniStat label="Today (incl. GST)" value={inr(r.today_sales)} />
              <MiniStat label="Monthly (incl. GST)" value={inr(r.monthly_sales)} />
              <MiniStat label="Orders" value={r.total_orders} />
              <MiniStat label="Distributors" value={r.assigned_distributors} />
              <MiniStat label="Salespersons" value={r.assigned_salespersons} />
              <MiniStat label="Pending" value={r.pending_orders} />
            </div>

            {/* 7-day sparkline */}
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Last 7 days</div>
              <Sparkline series={r.series_7d} />
            </div>
          </Card>
        ))}
      </div>

      {/* Comparison table */}
      {rows.length > 1 && (
        <Card className="mt-6 overflow-x-auto">
          <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900">Team Leader Comparison</div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Rank</TableHead><TableHead>Team Leader</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Monthly</TableHead>
              <TableHead className="text-right">Today</TableHead>
              <TableHead className="text-right">Orders</TableHead>
              <TableHead className="text-right">Pending</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r, idx) => (
                <TableRow key={r.team_leader_id}>
                  <TableCell className="font-bold text-slate-500">#{idx + 1}</TableCell>
                  <TableCell><div className="font-medium">{r.name}</div><div className="text-xs text-slate-500">{r.email}</div></TableCell>
                  <TableCell className="text-right font-semibold text-[#8a6600]">{inr(r.total_sales)}</TableCell>
                  <TableCell className="text-right">{inr(r.monthly_sales)}</TableCell>
                  <TableCell className="text-right">{inr(r.today_sales)}</TableCell>
                  <TableCell className="text-right">{r.total_orders}</TableCell>
                  <TableCell className="text-right">{r.pending_orders}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-semibold text-slate-900 truncate">{value}</div>
    </div>
  );
}

function Sparkline({ series }) {
  const max = Math.max(1, ...(series || []).map(s => s.sales));
  return (
    <div className="flex items-end gap-1 h-10">
      {(series || []).map((s, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${s.date}: ${inr(s.sales)}`}>
          <div className="w-full bg-[#faf6e6]0 rounded-sm" style={{ height: `${Math.max(2, (s.sales / max) * 100)}%`, opacity: s.sales ? 1 : 0.2 }} />
          <div className="text-[8px] text-slate-400 mt-0.5">{s.date.slice(-2)}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Owner — Distributor Sales Visibility (list of distributors → click for drilldown)
// ============================================================================
export function OwnerDistributorSalesListPage() {
  const nav = useNavigate();
  const [dists, setDists] = useState([]);
  useEffect(() => { dms.listDistributors().then(d => setDists(d.data || [])); }, []);
  return (
    <div>
      <PageHeader title="Distributor Sales Visibility" subtitle="See which retailers each distributor is selling to, and what products / quantities / prices" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {dists.map(d => (
          <Card key={d.id} className="p-4 hover:shadow-md cursor-pointer" onClick={() => nav(`/dms/owner/distributor-sales/${d.id}`)} data-testid={`ds-card-${d.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900">{d.name}</div>
                <div className="text-xs text-slate-500">{d.region}</div>
              </div>
              <ArrowUpRight size={16} className="text-[#a67c00]" />
            </div>
            <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">View retailers, products, prices <ChevronRight size={12} /></div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Owner — Distributor Sales Drilldown
// ============================================================================
export function OwnerDistributorSalesDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { dms.ownerDistributorSales(id).then(setData).catch(() => {}); }, [id]);

  if (!data) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  const { distributor, by_retailer, by_product, recent_orders, totals } = data;

  return (
    <div>
      <PageHeader title={distributor.name} subtitle={`Complete business activity · ${distributor.region || "—"}`} back="/dms/owner/distributor-sales" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KpiCard label="Total Revenue" value={inr(totals.revenue)} tint="bg-[#faf6e6] text-[#a67c00]" icon={TrendingUp} />
        <KpiCard label="Total Orders" value={totals.orders} tint="bg-blue-50 text-blue-700" icon={Package} />
        <KpiCard label="Active Retailers" value={totals.retailers_active} tint="bg-amber-50 text-amber-700" icon={Store} />
        <KpiCard label="Products Sold" value={totals.products_sold} tint="bg-indigo-50 text-indigo-700" icon={Package} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Retailer */}
        <Card>
          <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900 flex items-center gap-2"><Store size={16} /> Retailers</div>
          <Table>
            <TableHeader><TableRow><TableHead>Retailer</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
            <TableBody>
              {by_retailer.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-slate-400">No sales yet</TableCell></TableRow>}
              {by_retailer.map(r => (
                <TableRow key={r.retailer_id}>
                  <TableCell>
                    <div className="font-medium">{r.retailer_name}</div>
                    <div className="text-xs text-slate-500 truncate max-w-xs">{r.address}</div>
                  </TableCell>
                  <TableCell className="text-right">{r.orders}</TableCell>
                  <TableCell className="text-right font-semibold">{inr(r.total_revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* By Product */}
        <Card>
          <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900 flex items-center gap-2"><Package size={16} /> Products</div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Product</TableHead>
              <TableHead className="text-right">Qty (Box/Pcs)</TableHead>
              <TableHead className="text-right">Prices</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {by_product.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">No products sold yet</TableCell></TableRow>}
              {by_product.map(p => (
                <TableRow key={p.product_id}>
                  <TableCell>
                    <div className="font-medium">{p.product_name}</div>
                    <div className="text-xs font-mono text-slate-500">{p.sku_code}</div>
                  </TableCell>
                  <TableCell className="text-right text-sm">{p.qty_boxes} / {p.qty_pcs}</TableCell>
                  <TableCell className="text-right text-xs text-slate-600">{p.prices_seen.map(x => inr(x)).join(", ") || "—"}</TableCell>
                  <TableCell className="text-right font-semibold">{inr(p.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Recent orders */}
      <Card className="mt-5">
        <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-900">Recent Orders (last 30)</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Order</TableHead><TableHead>Retailer</TableHead>
            <TableHead className="text-right">Items</TableHead><TableHead className="text-right">Total</TableHead>
            <TableHead>Status</TableHead><TableHead>Placed</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {recent_orders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No orders</TableCell></TableRow>}
            {recent_orders.map(o => (
              <TableRow key={o.id}>
                <TableCell className="font-mono text-xs">{o.id}</TableCell>
                <TableCell>{o.retailer}</TableCell>
                <TableCell className="text-right">{o.items_count}</TableCell>
                <TableCell className="text-right font-medium">{inr(o.total)}</TableCell>
                <TableCell><span className="text-xs">{o.status}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(o.at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, tint, icon: Icon }) {
  return (
    <Card className="p-4">
      <div className={`inline-flex h-9 w-9 rounded-lg items-center justify-center mb-2 ${tint}`}><Icon size={18} /></div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    </Card>
  );
}
